import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, LinearProgress, MenuItem, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DriverDetails } from '../types/predictions';
import { getSimModelCheck, ModelCheckRace, SimModel } from '../services/api';
import { SegmentedToggle } from './SegmentedToggle';

// Categorical line colors (validated for color-vision deficiency and contrast against each theme's surface; slots in
// fixed order, never cycled). Three light-mode slots are below 3:1 on white, so lines also get direct end labels and
// the race table below is the text view.
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];
const MAX_SERIES = SERIES_LIGHT.length;

type StatKey = keyof DriverDetails | 'Luck';

interface StatDef {
  key: StatKey;
  label: string;
  /** Positions: 1 is best, so the axis is flipped (better is always up). */
  lowerIsBetter?: boolean;
  /** Centered on zero (a difference rather than a position or count). */
  signed?: boolean;
  /** Derived value; defaults to the race field named by `key`. */
  get?: (r: DriverDetails) => number;
  format: (v: number) => string;
}

const pct = (v: number) => `${Math.round(v * 100)}%`;
const int = (v: number) => `${Math.round(v)}`;
const STATS: StatDef[] = [
  { key: 'Pos', label: 'Finish', lowerIsBetter: true, format: int },
  { key: 'St', label: 'Start', lowerIsBetter: true, format: int },
  { key: 'Avg', label: 'Average running position', lowerIsBetter: true, format: (v) => v.toFixed(1) },
  { key: 'DriverRating', label: 'Driver rating', format: int },
  { key: 'PctT15Lap', label: '% of laps in the top 15', format: pct },
  { key: 'Led', label: 'Laps led', format: int },
  { key: 'Fastest', label: 'Fastest laps', format: int },
  { key: 'QPass', label: 'Quality passes', format: int },
  { key: 'PassDif', label: 'Pass differential', format: (v) => (v > 0 ? `+${Math.round(v)}` : int(v)) },
  {
    // Finish minus average running position: positive = finished worse than they ran (results lost to trouble).
    key: 'Luck',
    label: 'Finish vs where they ran',
    lowerIsBetter: true,
    signed: true,
    get: (r) => r.Pos - r.Avg,
    format: (v) => {
      const n = Number.isInteger(v) ? String(v) : v.toFixed(1);
      return v > 0 ? `+${n}` : n;
    },
  },
];
const statValue = (stat: StatDef, r: DriverDetails) =>
  stat.get ? stat.get(r) : Number(r[stat.key as keyof DriverDetails]);

interface Point {
  driver: string;
  date: number;
  value: number;
  /** Average of this and the previous races (up to AVG_WINDOW). */
  avg: number;
  /** The live model's backtested predicted finish for this race, when there is one. */
  pred?: number;
  /** Average running position, and its rolling average (the "where they ran" line). */
  ran: number;
  ranAvg: number;
  race: DriverDetails;
}

interface RaceTrendChartProps {
  driversDetails: Record<string, DriverDetails[]>;
  /** The simulated track's key; enables the "This track" filter. */
  trackKey?: string;
  /** The Simulator's model; enables "vs model" (the model's backtested predictions) for finishes. */
  model?: SimModel;
}

const HEIGHT = 260;
const M = { top: 24, right: 92, bottom: 28, left: 40 };
const AVG_WINDOW = 5;

const niceTicks = (lo: number, hi: number, count = 5): number[] => {
  const span = hi - lo || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => span / s <= count) ?? raw;
  const out: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step) out.push(Number(t.toFixed(6)));
  return out;
};

const shortDate = (t: number) => {
  const d = new Date(t);
  return `${d.toLocaleDateString(undefined, { month: 'short' })} '${String(d.getFullYear()).slice(2)}`;
};
const longDate = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const lastName = (name: string) =>
  name
    .split(' ')
    .filter((w) => !/^(jr|sr|ii|iii)\.?$/i.test(w))
    .pop() ?? name;

/** Race-by-race line chart for one or more drivers: pick a stat and which drivers to show; hover for details. */
export const RaceTrendChart: React.FC<RaceTrendChartProps> = ({ driversDetails, trackKey, model }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const colors = dark ? SERIES_DARK : SERIES_LIGHT;
  const surface = theme.palette.background.paper;
  const grid = dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
  const axisText = theme.palette.text.secondary;

  const drivers = useMemo(() => Object.keys(driversDetails).slice(0, MAX_SERIES), [driversDetails]);
  // Color follows the driver, not their position in the visible set.
  const colorOf = useMemo(() => new Map(drivers.map((d, i) => [d, colors[i]])), [drivers, colors]);

  const [statKey, setStatKey] = useState<StatKey>('Pos');
  const [scope, setScope] = useState<'all' | 'track'>('all');
  const [smooth, setSmooth] = useState<'avg' | 'raw'>('avg');
  const [vsModel, setVsModel] = useState(false);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hover, setHover] = useState<Point | null>(null);
  const stat = STATS.find((s) => s.key === statKey) ?? STATS[0];

  // Loaded only when "vs model" is switched on (one small query per driver set and model).
  const showModel = vsModel && !!model && stat.key === 'Pos';
  const modelCheck = useQuery<ModelCheckRace[], Error>({
    queryKey: ['simModelCheck', drivers, model],
    queryFn: () => getSimModelCheck(drivers, model),
    enabled: showModel && drivers.length > 0,
    staleTime: 60 * 60 * 1000,
  });
  const predOf = useMemo(
    () => new Map((modelCheck.data ?? []).map((m) => [`${m.Driver}|${m.Date}|${m.Site}`, m.PredFinish])),
    [modelCheck.data],
  );

  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(280, Math.floor(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const series = useMemo(
    () =>
      drivers
        .filter((d) => !hidden.has(d))
        .map((d) => ({
          driver: d,
          points: (driversDetails[d] ?? [])
            .filter((r) => scope === 'all' || !trackKey || r.TrackKey === trackKey)
            .map((r) => ({
              driver: d,
              date: new Date(r.Date).getTime(),
              value: statValue(stat, r),
              avg: 0,
              ran: Number(r.Avg),
              ranAvg: 0,
              pred: predOf.get(`${d}|${r.Date}|${r.Site}`),
              race: r,
            }))
            .filter((p) => Number.isFinite(p.value) && Number.isFinite(p.date))
            .sort((a, b) => a.date - b.date)
            .map((p, i, arr): Point => {
              const w = arr.slice(Math.max(0, i - AVG_WINDOW + 1), i + 1);
              return {
                ...p,
                avg: w.reduce((t, q) => t + q.value, 0) / w.length,
                ranAvg: w.reduce((t, q) => t + q.ran, 0) / w.length,
              };
            }),
        }))
        .filter((s) => s.points.length > 0),
    [drivers, hidden, driversDetails, scope, trackKey, stat, predOf],
  );
  const all = series.flatMap((s) => s.points);

  if (drivers.length === 0) return null;

  // One driver on Finish: add where they ran, and shade the races they finished worse than they ran.
  const showRan = stat.key === 'Pos' && series.length === 1;
  const plotW = width - M.left - ((series.length > 1 && series.length <= 4) || showRan ? M.right : 16);
  const plotH = HEIGHT - M.top - M.bottom;
  const tMin = Math.min(...all.map((p) => p.date));
  const tMax = Math.max(...all.map((p) => p.date));
  const vals = all.flatMap((p) => (showRan ? [p.value, p.ran] : [p.value]));
  const vMinRaw = Math.min(...vals);
  const vMaxRaw = Math.max(...vals);
  // Signed stats sit symmetrically around zero so "better" and "worse" read the same distance from the line.
  const vAbs = Math.max(1, ...vals.map(Math.abs));
  const vMin = stat.signed ? -vAbs : stat.lowerIsBetter ? Math.max(1, Math.floor(vMinRaw)) : Math.min(0, vMinRaw);
  const vMax = stat.signed ? vAbs : vMaxRaw === vMin ? vMin + 1 : vMaxRaw;
  const x = (t: number) => M.left + (tMax === tMin ? plotW / 2 : ((t - tMin) / (tMax - tMin)) * plotW);
  // Better is always up: positions put 1 at the top.
  const y = (v: number) =>
    stat.lowerIsBetter
      ? M.top + ((v - vMin) / (vMax - vMin)) * plotH
      : M.top + plotH - ((v - vMin) / (vMax - vMin)) * plotH;
  const yTicks = niceTicks(vMin, vMax);
  const xTicks = (() => {
    const n = Math.max(2, Math.min(6, Math.floor(plotW / 110)));
    return Array.from({ length: n }, (_, i) => tMin + ((tMax - tMin) * i) / (n - 1));
  })();

  const lineValue = (p: Point) => (smooth === 'avg' ? p.avg : p.value);
  const ranValue = (p: Point) => (smooth === 'avg' ? p.ranAvg : p.ran);
  // The model line: each race's prediction, or the same rolling average as the actual line.
  const predLines = showModel
    ? series.map((s) => {
        const withPred = s.points.filter((p) => p.pred != null);
        return {
          driver: s.driver,
          points: withPred.map((p, i) => {
            const w = withPred.slice(Math.max(0, i - AVG_WINDOW + 1), i + 1);
            return {
              date: p.date,
              v: smooth === 'avg' ? w.reduce((t, q) => t + (q.pred ?? 0), 0) / w.length : p.pred!,
            };
          }),
        };
      })
    : [];
  // Per driver: average of (predicted - actual) finish over races with a prediction; positive = beat the model.
  const beat = showModel
    ? series
        .map((s) => {
          const withPred = s.points.filter((p) => p.pred != null);
          return {
            driver: s.driver,
            n: withPred.length,
            diff: withPred.reduce((t, p) => t + (p.pred! - p.value), 0) / Math.max(1, withPred.length),
          };
        })
        .filter((b) => b.n > 0)
    : [];

  // Single-driver luck view: label the last value of each line (finish, ran, model), nudged apart.
  const luckLabels = showRan
    ? (() => {
        const pts = series[0].points;
        const last = pts[pts.length - 1];
        const predPts = predLines[0]?.points ?? [];
        return [
          { label: 'finish', v: lineValue(last), bold: true },
          { label: 'ran', v: ranValue(last), bold: false },
          ...(showModel && predPts.length ? [{ label: 'model', v: predPts[predPts.length - 1].v, bold: false }] : []),
        ]
          .map((l) => ({ ...l, y: y(l.v) }))
          .sort((a, b) => a.y - b.y)
          .reduce<{ label: string; v: number; bold: boolean; y: number }[]>((acc, l) => {
            const prev = acc[acc.length - 1];
            acc.push({ ...l, y: prev && l.y - prev.y < 14 ? prev.y + 14 : l.y });
            return acc;
          }, []);
      })()
    : [];

  // Luck stat: clicking a driver's line opens their single-driver finish view.
  const focusDriver = (d: string) => {
    setStatKey('Pos');
    setHidden(new Set(drivers.filter((o) => o !== d)));
  };

  // Direct end labels for up to four lines, nudged apart so they don't collide.
  const endLabels =
    series.length > 1 && series.length <= 4
      ? series
          .map((s) => ({ driver: s.driver, y: y(lineValue(s.points[s.points.length - 1])) }))
          .sort((a, b) => a.y - b.y)
          .reduce<{ driver: string; y: number }[]>((acc, l) => {
            const prev = acc[acc.length - 1];
            acc.push({ ...l, y: prev && l.y - prev.y < 14 ? prev.y + 14 : l.y });
            return acc;
          }, [])
      : [];

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    let best: Point | null = null;
    let bestD = Infinity;
    for (const p of all) {
      const d = (x(p.date) - mx) ** 2 + (y(p.value) - my) ** 2 / 4; // favor the nearest race in time
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    setHover(bestD < 60 ** 2 ? best : null);
  };

  // Other visible drivers in the same race as the hovered point (same date and track), for the tooltip.
  const sameRace = hover
    ? series
        .map((s) => ({
          driver: s.driver,
          p: s.points.find((p) => p.race.Date === hover.race.Date && p.race.Site === hover.race.Site),
        }))
        .filter((r): r is { driver: string; p: Point } => !!r.p)
    : [];

  const toggleDriver = (d: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });

  return (
    <Box sx={{ mb: 2 }}>
      {/* Controls in one row above the chart. */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <TextField
          select
          size="small"
          label="Stat"
          value={statKey}
          onChange={(e) => setStatKey(e.target.value as StatKey)}
          sx={{ minWidth: 220 }}
        >
          {STATS.map((s) => (
            <MenuItem key={s.key} value={s.key}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        {trackKey && (
          <SegmentedToggle
            aria-label="Races"
            size="small"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'all', label: 'All races' },
              { value: 'track', label: 'This track' },
            ]}
          />
        )}
        <SegmentedToggle
          aria-label="Line"
          size="small"
          value={smooth}
          onChange={setSmooth}
          options={[
            { value: 'avg', label: `${AVG_WINDOW}-race average` },
            { value: 'raw', label: 'Each race' },
          ]}
        />
        {model && stat.key === 'Pos' && (
          <SegmentedToggle
            aria-label="Compare with the model"
            size="small"
            value={vsModel ? 'on' : 'off'}
            onChange={(v: string) => setVsModel(v === 'on')}
            options={[
              { value: 'off', label: 'Actual' },
              { value: 'on', label: 'vs model' },
            ]}
          />
        )}
        {drivers.length > 1 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap role="group" aria-label="Drivers shown">
            {drivers.map((d) => {
              const on = !hidden.has(d);
              return (
                <Box
                  key={d}
                  component="button"
                  type="button"
                  onClick={() => toggleDriver(d)}
                  aria-pressed={on}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'transparent',
                    color: on ? 'text.primary' : 'text.disabled',
                    font: 'inherit',
                    fontSize: '0.8125rem',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: on ? colorOf.get(d) : 'transparent',
                      border: 2,
                      borderColor: colorOf.get(d),
                    }}
                  />
                  {d}
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Box ref={wrapRef} sx={{ position: 'relative', width: '100%' }}>
        {all.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No races to show{scope === 'track' ? ' at this track' : ''}.
          </Typography>
        ) : (
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label={`${stat.label} by race for ${series.map((s) => s.driver).join(', ')}`}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
            style={{ display: 'block', overflow: 'visible' }}
          >
            {/* Recessive hairline grid and axis labels. */}
            {yTicks.map((t) => (
              <g key={`y${t}`}>
                <line x1={M.left} x2={M.left + plotW} y1={y(t)} y2={y(t)} stroke={grid} strokeWidth={1} />
                <text x={M.left - 6} y={y(t)} dy="0.32em" textAnchor="end" fontSize={11} fill={axisText}>
                  {stat.format(t)}
                </text>
              </g>
            ))}
            {xTicks.map((t, i) => (
              <text key={`x${i}`} x={x(t)} y={HEIGHT - 8} textAnchor="middle" fontSize={11} fill={axisText}>
                {shortDate(t)}
              </text>
            ))}
            <text x={M.left} y={10} fontSize={10} fill={axisText}>
              {stat.signed
                ? '↑ finished better than they ran · ↓ finished worse'
                : stat.lowerIsBetter
                  ? '↑ better (1st at top)'
                  : '↑ better'}
            </text>
            {stat.signed && (
              <line x1={M.left} x2={M.left + plotW} y1={y(0)} y2={y(0)} stroke={axisText} strokeOpacity={0.7} />
            )}

            {hover && (
              <line
                x1={x(hover.date)}
                x2={x(hover.date)}
                y1={M.top}
                y2={M.top + plotH}
                stroke={axisText}
                strokeOpacity={0.5}
                strokeWidth={1}
              />
            )}

            {showRan &&
              (() => {
                const pts = series[0].points;
                const c = colorOf.get(series[0].driver);
                const top = pts.map((p) => `${x(p.date)},${y(ranValue(p))}`);
                const bottom = [...pts].reverse().map((p) => `${x(p.date)},${y(Math.max(lineValue(p), ranValue(p)))}`);
                return (
                  <g>
                    {/* Shaded where the finish was worse than the running position: results lost, not pace. */}
                    <path
                      d={`M${top.join(' L')} L${bottom.join(' L')} Z`}
                      fill={theme.palette.text.primary}
                      fillOpacity={0.09}
                    />
                    <polyline
                      points={top.join(' ')}
                      fill="none"
                      stroke={c}
                      strokeOpacity={0.55}
                      strokeWidth={1.5}
                      strokeLinejoin="round"
                    />
                  </g>
                );
              })()}

            {series.map((s) => {
              const c = colorOf.get(s.driver);
              return (
                <g key={s.driver}>
                  {showModel && (
                    // The model's predicted finish: same color, dashed, behind the actual line.
                    <polyline
                      points={(predLines.find((l) => l.driver === s.driver)?.points ?? [])
                        .map((p) => `${x(p.date)},${y(p.v)}`)
                        .join(' ')}
                      fill="none"
                      stroke={c}
                      strokeOpacity={0.75}
                      strokeWidth={1.75}
                      strokeDasharray="5 4"
                      strokeLinejoin="round"
                    />
                  )}
                  {smooth === 'raw' && (
                    <polyline
                      points={s.points.map((p) => `${x(p.date)},${y(lineValue(p))}`).join(' ')}
                      fill="none"
                      stroke={c}
                      strokeWidth={2}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  )}
                  {s.points.map((p, i) => (
                    <circle
                      key={i}
                      cx={x(p.date)}
                      cy={y(p.value)}
                      r={hover === p ? 5.5 : smooth === 'avg' ? 3 : 4}
                      // Each race's actual result; faint behind the average line. DNFs are hollow markers.
                      fill={p.race.DNF ? surface : c}
                      fillOpacity={smooth === 'avg' && hover !== p ? 0.35 : 1}
                      stroke={p.race.DNF ? c : surface}
                      strokeOpacity={smooth === 'avg' && hover !== p ? 0.5 : 1}
                      strokeWidth={smooth === 'avg' ? 1.5 : 2}
                    />
                  ))}
                  {smooth === 'avg' && (
                    <polyline
                      points={s.points.map((p) => `${x(p.date)},${y(p.avg)}`).join(' ')}
                      fill="none"
                      stroke={c}
                      strokeWidth={2.5}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  )}
                  {stat.key === 'Luck' && series.length > 1 && (
                    // Wider invisible stroke: click a driver's line to open their finish vs ran view.
                    <polyline
                      points={s.points.map((p) => `${x(p.date)},${y(lineValue(p))}`).join(' ')}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={12}
                      style={{ cursor: 'pointer' }}
                      onClick={() => focusDriver(s.driver)}
                    />
                  )}
                </g>
              );
            })}

            {luckLabels.map((l) => (
              <text
                key={`luck${l.label}`}
                x={M.left + plotW + 8}
                y={l.y}
                dy="0.32em"
                fontSize={12}
                fontWeight={l.bold ? 600 : 400}
                fill={theme.palette.text.primary}
              >
                {l.label} {l.v.toFixed(1)}
              </text>
            ))}
            {endLabels.map((l) => (
              <g key={`lbl${l.driver}`}>
                <circle cx={M.left + plotW + 10} cy={l.y} r={4} fill={colorOf.get(l.driver)} />
                <text x={M.left + plotW + 18} y={l.y} dy="0.32em" fontSize={12} fill={theme.palette.text.primary}>
                  {lastName(l.driver)}
                </text>
              </g>
            ))}
          </svg>
        )}

        {hover && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: Math.min(Math.max(x(hover.date) + 12, 8), width - 260),
              width: 250,
              pointerEvents: 'none',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: 3,
              p: 1,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {longDate(hover.date)} · {hover.race.Site}
            </Typography>
            {sameRace.map((r) => (
              <Stack key={r.driver} direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorOf.get(r.driver), flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {r.driver}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {stat.format(r.p.value)}
                  {r.p.race.DNF ? ' (DNF)' : ''}
                  {showRan && (
                    <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                      {' '}
                      · ran {r.p.ran.toFixed(0)}
                    </Box>
                  )}
                  {showModel
                    ? r.p.pred != null && (
                        <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                          {' '}
                          · model {r.p.pred.toFixed(1)}
                        </Box>
                      )
                    : smooth === 'avg' && (
                        <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                          {' '}
                          · avg {stat.format(r.p.avg)}
                        </Box>
                      )}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
      {showModel && (
        <Box sx={{ mt: 0.5, mb: 1 }}>
          {modelCheck.isLoading && <LinearProgress sx={{ my: 1, maxWidth: 240 }} />}
          {modelCheck.error && (
            <Typography variant="body2" color="error">
              {modelCheck.error.message}
            </Typography>
          )}
          {modelCheck.data && (
            <>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 0.5, display: showRan ? 'none' : 'flex' }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <svg width={22} height={6} aria-hidden>
                    <line x1={0} x2={22} y1={3} y2={3} stroke={axisText} strokeWidth={2.5} />
                  </svg>
                  <Typography variant="caption" color="text.secondary">
                    actual
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <svg width={22} height={6} aria-hidden>
                    <line x1={0} x2={22} y1={3} y2={3} stroke={axisText} strokeWidth={1.75} strokeDasharray="5 4" />
                  </svg>
                  <Typography variant="caption" color="text.secondary">
                    model's prediction
                  </Typography>
                </Stack>
              </Stack>
              {beat.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  None of these races have a model prediction (the model was tested on races from 2022 on).
                </Typography>
              )}
              {beat.map((b) => (
                <Stack key={b.driver} direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorOf.get(b.driver), flexShrink: 0 }}
                  />
                  <Typography variant="body2">
                    {Math.abs(b.diff) < 0.5 ? (
                      <>
                        {b.driver}{' '}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          matched the model
                        </Box>{' '}
                        (within half a spot, {b.n} races)
                      </>
                    ) : (
                      <>
                        {b.driver} finished{' '}
                        <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {Math.abs(b.diff).toFixed(1)} spots {b.diff >= 0 ? 'better' : 'worse'}
                        </Box>{' '}
                        than the model predicted ({b.n} races)
                      </>
                    )}
                  </Typography>
                </Stack>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                The model's prediction is its average simulated finish for each race, re-created from only the races
                before it (today's model, tested on every race since 2022).
              </Typography>
            </>
          )}
        </Box>
      )}
      {showRan && all.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
          {[
            {
              label: 'finish',
              el: <line x1={0} x2={22} y1={3} y2={3} stroke={colorOf.get(series[0].driver)} strokeWidth={2.5} />,
            },
            {
              label: 'where they ran (average running position)',
              el: (
                <line
                  x1={0}
                  x2={22}
                  y1={3}
                  y2={3}
                  stroke={colorOf.get(series[0].driver)}
                  strokeOpacity={0.55}
                  strokeWidth={1.5}
                />
              ),
            },
            ...(showModel
              ? [
                  {
                    label: "model's prediction",
                    el: (
                      <line
                        x1={0}
                        x2={22}
                        y1={3}
                        y2={3}
                        stroke={colorOf.get(series[0].driver)}
                        strokeOpacity={0.75}
                        strokeWidth={1.75}
                        strokeDasharray="5 4"
                      />
                    ),
                  },
                ]
              : []),
            {
              label: 'finished worse than they ran',
              el: (
                <rect x={0} y={0} width={22} height={6} rx={1} fill={theme.palette.text.primary} fillOpacity={0.15} />
              ),
            },
          ].map((l) => (
            <Stack key={l.label} direction="row" spacing={0.75} alignItems="center">
              <svg width={22} height={6} aria-hidden>
                {l.el}
              </svg>
              <Typography variant="caption" color="text.secondary">
                {l.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
      {stat.key === 'Pos' && series.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Show one driver to see where they ran vs where they finished, or pick “Finish vs where they ran” to compare
          everyone.
        </Typography>
      )}
      {stat.key === 'Luck' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Finish minus average running position. Below the line: finished worse than they ran (wrecks, strategy, late
          trouble); a stretch there with good pace is a bounce-back sign. Above: results better than their pace.
          {series.length > 1 ? ' Click a driver’s line to see their finish vs where they ran.' : ''}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Races behind the rating, oldest to newest.{' '}
        {smooth === 'avg' ? `Lines show each driver's last-${AVG_WINDOW}-race average; dots are single races. ` : ''}
        Hollow points are DNFs. The table below has every number.
      </Typography>
    </Box>
  );
};
